import { ReactElement, useEffect, useMemo, useRef, useState } from 'react'
import {
  get2DLineOrthoDirection,
  infiniteLineIntersection,
  projectPointOntoPlane,
} from '@editorUtils'
import { DrawControllerWrapper, TargetPlane, toVector2, useTapelineSnapTargets } from '@scene'
import { isNull } from 'lodash-es'
import { Plane as PlaneThree } from 'three'
import {
  ImmutableVector3,
  TransientDrawState,
  InteractiveLine,
  ScalingIndicator,
  InteractiveLineRef,
  InteractiveLineHandles,
} from '@modugen/ui-toolkit'
import { useControlStore } from '@editorStores'
import { useClickActionEnabled, useHideElements } from '@editorHooks'
import { nthLooped } from '@utils'
import { useModelSnapTargets } from '../../../FloorplanDrawer/hooks'
import { RoofFormSchema } from '../RoofForm/schema'

interface Props {
  points: RoofFormSchema['points']
  updatePoints: (points: RoofFormSchema['points']) => void
  activePointIndex: number | null
  activeElement: string | null
  zComputed: boolean
}

const RoofDrawing = ({
  points,
  updatePoints,
  activePointIndex,
  activeElement,
  zComputed,
}: Props): ReactElement => {
  const edgeLineRefs = useRef<Array<InteractiveLineRef>>([])

  const [activeEdgeIndex, setActiveEdgeIndex] = useState<null | number>(null)

  // MEMOS

  const roofPoints = useMemo(() => {
    return points?.map(p => new ImmutableVector3(p.x, p.y, p.z))
  }, [points])

  const roofPlane = useMemo(() => {
    if (!roofPoints || roofPoints.length < 3) return undefined
    return new PlaneThree().setFromCoplanarPoints(roofPoints[0].v, roofPoints[1].v, roofPoints[2].v)
  }, [roofPoints])

  const roofEdges = useMemo(
    () =>
      roofPoints?.map((p, i) => {
        const nextP = nthLooped(roofPoints, i + 1)

        return [p, nextP]
      }),
    [roofPoints],
  )

  // EFFECTS

  const snapToCornersAndEdges = useControlStore(state => state.snapToCornersAndEdges)
  const snapOrthogonal = useControlStore(state => state.snapOrthogonal)
  const setElementSelectionDisabled = useControlStore(state => state.setElementSelectionDisabled)

  // Get tapeline snap targets
  const tapelineSnapTargets = useTapelineSnapTargets(false)
  const modelSnapTargets = useModelSnapTargets({})

  // Combine all snap targets
  const allSnapTargets = useMemo(
    () => [...modelSnapTargets, ...tapelineSnapTargets],
    [modelSnapTargets, tapelineSnapTargets],
  )

  useHideElements(activeElement)

  // EVENTS
  const moveRoofEdge = (state: TransientDrawState) => {
    if (isNull(activeEdgeIndex) || !state.drawPoint || !roofPlane) return

    // What we basically do here is use the current line to create a plane that
    // is vertical and extend the previous and next line to find any
    // intersection point on the plane to update the corner points of the roof

    const edge = edgeLineRefs.current[activeEdgeIndex]

    if (!edge.transientLine.current) return

    if (roofEdges) {
      const [edgeStart, edgeEnd] = roofEdges[activeEdgeIndex]

      const edgeOrthogonalDirection2 = get2DLineOrthoDirection(
        toVector2(edgeStart),
        toVector2(edgeEnd),
      )

      const edgeOrthogonalDirection = new ImmutableVector3(
        edgeOrthogonalDirection2.x,
        edgeOrthogonalDirection2.y,
      )

      const cuttingPlane = new PlaneThree().setFromNormalAndCoplanarPoint(
        edgeOrthogonalDirection.v,
        state.drawPoint.v,
      )

      const [previousEdgeStart, previousEdgeEnd] = nthLooped(roofEdges, activeEdgeIndex - 1)
      const [nextEdgeStart, nextEdgeEnd] = nthLooped(roofEdges, activeEdgeIndex + 1)

      const intersectionPrevious = infiniteLineIntersection(
        cuttingPlane,
        previousEdgeStart,
        previousEdgeEnd,
      )

      const intersectionNext = infiniteLineIntersection(cuttingPlane, nextEdgeStart, nextEdgeEnd)

      if (intersectionPrevious && intersectionNext) {
        // In order to pass the checks by the backend the point must be
        // projected exactly onto the plane defined by the existing points of
        // the roof slab
        const intersectionPreviousProjected = projectPointOntoPlane(
          roofPlane,
          new ImmutableVector3(
            intersectionPrevious.x,
            intersectionPrevious.y,
            intersectionPrevious.z,
          ),
        )

        const intersectionNextProjected = projectPointOntoPlane(
          roofPlane,
          new ImmutableVector3(intersectionNext.x, intersectionNext.y, intersectionNext.z),
        )

        const previousLineRef = nthLooped(edgeLineRefs.current, activeEdgeIndex - 1)
        if (intersectionPrevious) {
          previousLineRef.updateHandle(InteractiveLineHandles.End, intersectionPreviousProjected)
        }

        const nextLineRef = nthLooped(edgeLineRefs.current, activeEdgeIndex + 1)
        if (intersectionNext) {
          nextLineRef.updateHandle(InteractiveLineHandles.Start, intersectionNextProjected)
        }

        edge.updateLine([intersectionPreviousProjected, intersectionNextProjected])
      }
    }
  }

  const drawEnd = () => {
    setElementSelectionDisabled(false)

    if (!activeElement || !roofEdges || isNull(activeEdgeIndex)) return

    const roofPoints = edgeLineRefs.current.map(line => {
      if (line?.transientLine.current) {
        return line.transientLine.current[0]
      }
      return roofEdges[activeEdgeIndex][0]
    })

    const newRoofPoints = roofPoints?.map(p => {
      return {
        x: p.x,
        y: p.y,
        z: zComputed && roofPlane ? projectPointOntoPlane(roofPlane, p).z : p.z,
      }
    })

    updatePoints(newRoofPoints)

    setActiveEdgeIndex(null)
  }

  const drawControllerEnabled = useClickActionEnabled()

  useEffect(() => {
    return () => setElementSelectionDisabled(false)
  }, [setElementSelectionDisabled])

  return (
    <>
      {/* DrawController used for moving the edges of an existing roof shape */}
      <DrawControllerWrapper
        enabled={!isNull(activeEdgeIndex) && drawControllerEnabled}
        onMouseMove={moveRoofEdge}
        onDrawEnd={drawEnd}
        enableIndicator={false}
        isValidDrawTarget={object => object.name === 'draw-plane'}
        additionalSnapTargets={allSnapTargets}
        snapToCornersAndEdges={snapToCornersAndEdges}
        orthoSnap={snapOrthogonal}
      />

      {/* Showing the corner the user is currently editing in the scene (if textfield is active in the form) */}
      <ScalingIndicator
        position={roofPoints?.[activePointIndex || 0].v}
        color="blue"
        visible={!isNull(activePointIndex)}
      />

      {roofEdges?.map(([start, end], i) => (
        <InteractiveLine
          key={`${activeElement}+${i}-${start.x}-${start.y}-${start.z}-${end.x}-${end.y}-${end.z}-`}
          ref={element => {
            edgeLineRefs.current[i] = element as InteractiveLineRef
          }}
          line={[start, end]}
          color={i === activeEdgeIndex ? 'blue' : '#707070'}
          disableDepthTest
          isSelected={activeEdgeIndex === i}
          onClick={() => {
            setActiveEdgeIndex(i)
            setElementSelectionDisabled(true)
          }}
          showHandles={false}
          nonSelectable={!isNull(activeEdgeIndex)}
          clickDisabled={!isNull(activeEdgeIndex)}
        />
      ))}

      {roofPoints && roofPoints?.length >= 3 && <TargetPlane points={roofPoints} />}
    </>
  )
}

export default RoofDrawing
